'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface User {
    id: string;
    nome: string;
    email: string;
    role: string;
    tenant_id?: string;
    permissions?: string[];
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (token: string, user: User) => void;
    logout: () => void;
    updateUser: (partial: Partial<User & Record<string, any>>) => void;
    isLoading: boolean;
}

/** Decodifica JWT e verifica se já expirou (sem biblioteca) */
function isTokenExpired(token: string): boolean {
    if (!token) return true;
    // Tokens offline gerados no PDV não são JWTs de servidor — considerá-los válidos
    if (token.startsWith('offline_')) return false;

    try {
        const parts = token.split('.');
        if (parts.length < 2) return false; // Token não-JWT personalizado é mantido
        const payload = JSON.parse(atob(parts[1]));
        if (!payload.exp) return false;
        return payload.exp * 1000 < Date.now();
    } catch {
        // Se falhar o parse mas for token offline ou sintético, não invalida
        return false; 
    }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);


export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Ref para o token atual — acessível no interceptor sem re-criar o effect
    const tokenRef = React.useRef<string | null>(null);
    tokenRef.current = token;

    useEffect(() => {
        const savedToken = localStorage.getItem('vsys_token');
        const savedUser = localStorage.getItem('vsys_user');

        try {
            if (savedToken && savedUser && savedUser !== 'undefined') {
                // Verifica expiração do JWT antes de restaurar sessão
                if (isTokenExpired(savedToken)) {
                    console.warn('[Auth] Token expirado — limpando sessão');
                    localStorage.removeItem('vsys_token');
                    localStorage.removeItem('vsys_user');
                    document.cookie = `vsys_auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
                } else {
                    setToken(savedToken);
                    const parsedUser = JSON.parse(savedUser);
                    if (!parsedUser.permissions) parsedUser.permissions = [];
                    setUser(parsedUser);
                }
            }
        } catch (error) {
            console.error('Failed to parse saved user:', error);
            localStorage.removeItem('vsys_token');
            localStorage.removeItem('vsys_user');
            setToken(null);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const login = (newToken: string, newUser: User) => {
        localStorage.setItem('vsys_token', newToken);
        localStorage.setItem('vsys_user', JSON.stringify(newUser));
        document.cookie = `vsys_auth_token=${newToken}; path=/; max-age=86400; SameSite=Lax`;
        setToken(newToken);
        setUser(newUser);
    };

    const logout = useCallback(() => {
        localStorage.removeItem('vsys_token');
        localStorage.removeItem('vsys_user');
        localStorage.removeItem('vsys-store-context');
        localStorage.removeItem('vsys:store');
        document.cookie = `vsys_auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        setToken(null);
        setUser(null);
        // Redireciona para login independente do app (staff, web, pos, contador)
        // IMPORTANTE: No POS (pdv), NÃO fazer hard reload (window.location.href) pois
        // offline o DNS falha e causa ERR_NAME_NOT_RESOLVED. O POS cuida do redirect
        // via onLogout callback que usa router.replace (navegação SPA).
        if (typeof window !== 'undefined') {
            const isPosApp = window.location.hostname.includes('pdv') || window.location.port === '3006';
            if (isPosApp) {
                // POS: não redireciona — o onLogout callback do StatusBar/page cuida disso
                return;
            }
            const isAccountant = window.location.hostname.includes('contador') || window.location.hostname.includes('staff');
            const target = isAccountant ? '/' : '/login';
            if (window.location.pathname !== target) {
                window.location.href = target;
            }
        }
    }, []);

    /**
     * Interceptor global de fetch.
     * Só faz logout se TODAS as condições forem verdadeiras:
     * 1. Não é endpoint /auth/ (login com senha errada não desloga)
     * 2. Há uma sessão ativa (token no state via ref)
     * 3. O request enviou exatamente o token atual no header Authorization
     */
    useEffect(() => {
        const originalFetch = window.fetch;

        window.fetch = async (...args) => {
            try {
                const response = await originalFetch(...args);

                if (response.status === 401) {
                    const url = typeof args[0] === 'string'
                        ? args[0]
                        : (args[0] as Request)?.url ?? '';

                    // Regra 1: ignora endpoints de autenticação
                    if (url.includes('/auth/')) return response;

                    // Regra 2: sem sessão ativa, ignora
                    const currentToken = tokenRef.current;
                    if (!currentToken) return response;

                    // Regra 3: só desloga se o request usou o token atual
                    const initHeaders = (args[1] as RequestInit)?.headers;
                    const authHeader =
                        initHeaders instanceof Headers
                            ? initHeaders.get('Authorization')
                            : (initHeaders as Record<string, string>)?.['Authorization']
                              ?? (initHeaders as Record<string, string>)?.['authorization']
                              ?? '';

                    // Regra 4: se for o app do PDV ou requisição marcada para não deslogar, ignora logout automático por 401
                    const isPosApp = typeof window !== 'undefined' && 
                        (window.location.hostname.includes('pdv') || window.location.port === '3006');
                    
                    const noLogoutHeader =
                        initHeaders instanceof Headers
                            ? initHeaders.get('x-no-logout') || initHeaders.get('X-No-Auto-Logout')
                            : (initHeaders as Record<string, string>)?.['x-no-logout']
                              ?? (initHeaders as Record<string, string>)?.['X-No-Auto-Logout']
                              ?? '';

                    if (isPosApp || noLogoutHeader === 'true') {
                        console.warn('[Auth] Ignorando logout automático por 401 (App PDV / No-Logout ativo)');
                        return response;
                    }

                    if (typeof authHeader === 'string' && authHeader.includes(currentToken)) {
                        console.warn('[Auth] 401 com token vigente → sessão expirada no servidor');
                        logout();
                    }
                }

                return response;
            } catch (error) {
                // Apenas propaga a exceção de rede/offline original para que o chamador trate
                throw error;
            }
        };

        return () => { window.fetch = originalFetch; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logout]);

    const updateUser = (partial: Partial<User & Record<string, any>>) => {
        setUser((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, ...partial };
            localStorage.setItem('vsys_user', JSON.stringify(updated));
            return updated;
        });
    };

    return (
        <AuthContext.Provider value={{ user, token, login, logout, updateUser, isLoading }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
